import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { isNull } from "lodash";
import { alphabetical } from "radash";
import { lastValueFrom } from "rxjs";
import { map, tap } from "rxjs/operators";
import { MemberBackend } from "src/app/backend/api/member.backend";
import {
  MemberLight,
  MemberPosition
} from "src/app/backend/models/member/member";
import { appState } from "src/app/core/state";
import { getCustomerNominative } from "src/app/support/customer";
import { getSelectParamByLoadOptions } from "src/app/support/dx";
import { searchListOfObjects } from "src/app/support/search";

type Providers = {
  memberBackend: MemberBackend;
};
type Filters = {
  position?: MemberPosition;
  bookable?: boolean;
  tenant?: string;
};

export function buildStaffDataSource(
  providers: Providers,
  filters?: Filters
): DataSource {
  return new DataSource({
    paginate: false,
    store: new CustomStore({
      byKey: async (id: string | null) => {
        if (isNull(id)) {
          return undefined;
        }

        return await lastValueFrom(
          providers.memberBackend.loadLight(id).pipe(
            map((x) => ({
              ...x,
              value: x.id,
              display: getCustomerNominative(x, true)
            }))
          )
        );
      },

      load: (options) => {
        const { queryString } = getSelectParamByLoadOptions(options);
        const position = filters?.position;
        const bookableOnly = filters?.bookable === true;
        const tenant = filters?.tenant ?? appState.tenantId;

        const mapMembers = (members: MemberLight[]): MemberLight[] => {
          let result =
            position != null
              ? members.filter((m: MemberLight) => m.position === position)
              : members;
          result = bookableOnly
            ? result.filter((m: MemberLight) => m.bookable === true)
            : result;

          return result;
        };

        const formatMembers = (members: MemberLight[]) => {
          if (!queryString) {
            return members;
          }

          return searchListOfObjects(
            members,
            (x) => getCustomerNominative(x, true),
            queryString
          );
        };

        const toSelectItems = (members: MemberLight[]) =>
          alphabetical(members, (member: MemberLight) =>
            getCustomerNominative(member, true)
          ).map((member: MemberLight) => ({
            ...member,
            value: member.id,
            display: getCustomerNominative(member, true)
          }));

        if (!tenant) {
          return Promise.resolve([]);
        }

        if (appState.members.length > 0) {
          const members = toSelectItems(
            formatMembers(mapMembers(appState.members))
          );

          return Promise.resolve(members);
        }

        const loadMembers$ = position
          ? providers.memberBackend
              .listActiveAllByPositionLight(position, tenant)
              .pipe(map((members: MemberLight[]) => mapMembers(members)))
          : providers.memberBackend
              .listActiveAllLight(false, tenant)
              .pipe(map((members: MemberLight[]) => mapMembers(members)));

        return lastValueFrom(
          loadMembers$.pipe(
            map((members: MemberLight[]) => formatMembers(members)),
            map((members: MemberLight[]) => toSelectItems(members)),
            tap((members: MemberLight[]) => {
              appState.members = members;
            })
          )
        );
      }
    })
  });
}
